import type { i18n as I18n } from "i18next";
import { ROUTE_CONFIG } from "~/config/route-config";
import { resolveRouteFromPathname } from "~/lib/locale-path";
import { isOfflineFallbackPath, OFFLINE_DOCUMENT_NAMESPACES } from "~/lib/pwa/offline-fallback";

const i18nConfig = {
  supportedLngs: ["es", "en"],
  fallbackLng: "es",
  defaultNS: "common",
  ns: [
    "admin-insights",
    "common",
    "home",
    "phases",
    "legal",
    "player",
    "dashboard",    "auth",
    "assessment",
    "technique-library",
    "library",
    "free-practice",
    "support",
    "unsubscribe",
    "settings",
    "account",
    "profile",
    "explore",
    "journal",
    "journey",
    "onboarding",
    "consent",
    "health-consent",
    "recovery",
    "nav",
    "footer",
    "assets",
    "metodo",
    "faq",
    "diastasis",
    "guides",
    "instagram-copilot",
    "autotest",
    // T428 — the unified question bank's copy. Owned by NEITHER surface: `autotest` is
    // public-funnel chrome and `onboarding` is member chrome, and "both surfaces render
    // from the same i18n strings" needs a namespace neither can quietly diverge.
    "questionbank",
    "bonus",
    "testimonials",
    "feedback",
    "maintenance",
    "status",
    "error",
    "pwa",
  ],
  react: { useSuspense: false },
};


/** Public documents need their matched routes plus the chrome, not the member catalogue. */
export function documentNamespaces(pathname: string, matchedNamespaces: string[]): string[] {
  if (isOfflineFallbackPath(pathname)) return [...OFFLINE_DOCUMENT_NAMESPACES];
  const route = resolveRouteFromPathname(pathname);
  if (!route || ROUTE_CONFIG[route.routeKey].isPrivate) return [...i18nConfig.ns];
  return [...new Set([...OFFLINE_DOCUMENT_NAMESPACES, ...matchedNamespaces])];
}

/** A failed backend read counts as loaded in i18next, but contains no usable translation. */
export function hasRequiredTranslations(instance: I18n, namespaces: string[]): boolean {
  return namespaces.every((ns) => instance.languages.some((lng) => instance.hasResourceBundle(lng, ns)));
}

/** Retain useful SSR HTML on a failed public boot; private and offline boot stay unchanged. */
export function canHydrateDocument(pathname: string, instance: I18n, namespaces: string[]): boolean {
  const route = resolveRouteFromPathname(pathname);
  if (isOfflineFallbackPath(pathname) || !route || ROUTE_CONFIG[route.routeKey].isPrivate) return true;
  return hasRequiredTranslations(instance, namespaces);
}

export default i18nConfig;
